import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { 
  Scissors, 
  Calendar, 
  Star, 
  Gift, 
  MessageSquare, 
  Sparkles,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Check,
  Menu,
  X,
  Users,
  TrendingUp,
  Shield,
  Play,
  Crown,
  Heart,
  Award,
  Zap,
  Info,
  Globe,
  ShoppingCart,
  Beaker,
  ArrowRight,
  Bell,
  BarChart3,
  Smartphone,
  QrCode,
  Clock,
  Mail,
  Phone,
  DollarSign,
  FileText,
  Lock,
  Percent,
  RefreshCw,
  Building2,
  Briefcase,
  Share2,
  Instagram,
  Facebook,
  Twitter,
  Linkedin,
  HelpCircle,
  XCircle,
  CheckCircle,
  Banknote,
  CreditCard as CardIcon,
  Wallet
} from "lucide-react";
import { useAuth } from "@/App";
import AppIntroduction from "@/components/AppIntroduction";
import { HowItWorksSection } from "@/components/GetStartedGuide";
import ProfileDropdown from "@/components/ProfileDropdown";

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showIntro, setShowIntro] = useState(false);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Debug auth state
  useEffect(() => {
    console.log("LandingPage auth state:", { user: user?.email, loading });
  }, [user, loading]);

  // Auto-rotate hero images
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const heroImages = [
    {
      src: "https://static.prod-images.emergentagent.com/jobs/bf1d2e2c-705d-447e-ae32-aacc715b9ac1/images/f0c1edc2c96a785917bde77259ba2f93a96e2967cee19159f82a7fa89e55fada.png",
      alt: "Diverse salon team - Black and white stylists serving clients"
    },
    {
      src: "https://static.prod-images.emergentagent.com/jobs/bf1d2e2c-705d-447e-ae32-aacc715b9ac1/images/50540290f65f20a31cb688f78d06e1e06c5e02c0ed0085df0e322e949f369efa.png",
      alt: "Black and Latino barbers working side by side"
    },
    {
      src: "https://static.prod-images.emergentagent.com/jobs/bf1d2e2c-705d-447e-ae32-aacc715b9ac1/images/aa09c032ffc85dccfbfd842b12d50f2e47ac011aaacb702c33b462ca4759d16e.png",
      alt: "Full diverse barbershop team with tattoos and style"
    }
  ];

  // Top 8 features shown immediately
  const topFeatures = [
    {
      icon: Calendar,
      title: "Smart Scheduling",
      description: "AI-powered booking that optimizes your day and reduces no-shows by 40%."
    },
    {
      icon: Wallet,
      title: "All Payment Methods",
      description: "Accept Stripe, PayPal, Cash App, Zelle, Venmo - get paid your way."
    },
    {
      icon: Bell,
      title: "Automated Reminders",
      description: "SMS & email reminders reduce no-shows and keep clients on time."
    },
    {
      icon: Globe,
      title: "Pro Mini-Site",
      description: "Get your own professional website with custom URL and booking page.",
      isNew: true
    },
    {
      icon: QrCode,
      title: "Shareable QR Codes",
      description: "Generate QR codes for business cards, flyers, and social media."
    },
    {
      icon: Gift,
      title: "Loyalty Rewards",
      description: "Automated points system that keeps clients coming back."
    },
    {
      icon: Users,
      title: "Staff Management",
      description: "Track commissions, payroll, and manage your entire team.",
      isNew: true
    },
    {
      icon: BarChart3,
      title: "Reports & Analytics",
      description: "Track earnings, appointments, and client retention with visual charts.",
      isNew: true
    }
  ];

  // Additional features shown when expanded
  const moreFeatures = [
    {
      icon: Building2,
      title: "Booth Rental Management",
      description: "Collect rent, track payments, send automated reminders to renters."
    },
    {
      icon: Share2,
      title: "Social Sharing",
      description: "Share your booking link on Instagram, Facebook, and more."
    },
    {
      icon: FileText,
      title: "Client Notes & CRM",
      description: "Store preferences, formulas, and notes for every client."
    },
    {
      icon: Clock,
      title: "Waitlist Management",
      description: "Manage cancellations and fill empty slots automatically."
    },
    {
      icon: Percent,
      title: "Promo Codes",
      description: "Create discounts, free months, and special offers for clients."
    },
    {
      icon: Shield,
      title: "Health Profiles",
      description: "Track allergies, medications, and sensitivities (HIPAA compliant)."
    },
    {
      icon: Briefcase,
      title: "Tax & Compliance",
      description: "W-9 tracking, 1099 prep, and payroll management for staff."
    },
    {
      icon: RefreshCw,
      title: "Recurring Appointments",
      description: "Set up standing appointments that repeat weekly or monthly."
    },
    {
      icon: Smartphone,
      title: "Mobile Optimized",
      description: "Full functionality on any device - manage your business anywhere."
    },
    {
      icon: Mail,
      title: "Email Marketing",
      description: "Send campaigns, promotions, and updates to your client list."
    },
    {
      icon: Star,
      title: "Reviews & Ratings",
      description: "Collect and showcase client reviews to build your reputation."
    },
    {
      icon: Lock,
      title: "Secure & Private",
      description: "Bank-level encryption protects all your business data."
    }
  ];

  const stats = [
    { value: "$7.5B+", label: "Haircare Market", icon: TrendingUp },
    { value: "10K+", label: "Professionals Trust Us", icon: Users },
    { value: "98%", label: "Client Satisfaction", icon: Heart },
    { value: "40%", label: "Revenue Increase", icon: Award }
  ];

  const testimonials = [
    {
      name: "Keisha Williams",
      role: "Master Stylist, Atlanta",
      image: "https://images.pexels.com/photos/3785079/pexels-photo-3785079.jpeg?auto=compress&w=150&h=150",
      quote: "MyHairChair understands our community. Finally, a platform built with us in mind. My bookings increased 60% in just 3 months."
    },
    {
      name: "Marcus Johnson",
      role: "Celebrity Barber, LA",
      image: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&w=150&h=150",
      quote: "The loyalty program is a game-changer. My clients love earning rewards, and I love seeing them come back every week."
    },
    {
      name: "Tanya Brooks",
      role: "Salon Owner, Houston",
      image: "https://images.pexels.com/photos/3756679/pexels-photo-3756679.jpeg?auto=compress&w=150&h=150",
      quote: "Managing 8 stylists used to be chaos. Now everything runs smoothly - booth rent, appointments, payments, all automated."
    }
  ];

  const pricingPlans = [
    {
      name: "Pro",
      price: "$29.99",
      period: "/month",
      description: "For barbers & stylists",
      popular: true,
      tagline: "Everything you need to grow",
      features: [
        "30-day FREE trial",
        "Unlimited clients",
        "CRM Dashboard",
        "Appointment reminders (SMS/Email)",
        "All payment methods (Zelle, CashApp)",
        "Pro Mini-Site (Custom URL)",
        "Shareable QR Code Cards",
        "Portfolio Gallery",
        "Loyalty Rewards Program",
        "Google Calendar Sync",
        "Advanced Analytics",
        "Priority Search Listing"
      ]
    },
    {
      name: "Elite",
      price: "$49.99",
      period: "/month",
      description: "For salon & shop owners",
      tagline: "Run your entire business",
      features: [
        "30-day FREE trial",
        "Everything in Pro",
        "Staff Management Dashboard",
        "Commission Tracking",
        "Payroll & W-9 Compliance",
        "Booth/Chair Rent Collection",
        "Team Analytics",
        "Full Reports & P&L",
        "Digital Marketing Toolkit",
        "Custom Branding Colors",
        "0% Platform Fees"
      ]
    }
  ];

  // Bottom Line / Transparency section
  const transparencyPoints = [
    {
      icon: DollarSign,
      title: "All profits are yours",
      description: "We take $0 from your online bookings. All revenue you make is 100% yours to keep."
    },
    {
      icon: XCircle,
      title: "No long-term contracts",
      description: "No contract fees, cancellation fees, setup fees, or hidden charges. Cancel anytime."
    },
    {
      icon: CheckCircle,
      title: "Only pay for what you need",
      description: "Start free, upgrade when ready. Add Reports ($10/mo) or go Elite for full features."
    },
    {
      icon: Gift,
      title: "Free trials on everything",
      description: "30-day free trial on Pro/Elite. 30-day free trial on Reports Add-On."
    }
  ];

  // FAQs
  const faqs = [
    {
      question: "Does MyHairChair take a percentage of my bookings?",
      answer: "No! We never take a cut of your earnings. Unlike other platforms that charge 20-30% per booking, we charge a flat monthly subscription. All the money you earn from appointments is 100% yours."
    },
    {
      question: "How does the free trial work?",
      answer: "Both Pro and Elite plans include a 30-day free trial. Our Reports Add-On also includes a 30-day free trial. No credit card required to start - cancel anytime during the trial."
    },
    {
      question: "Can I cancel my subscription anytime?",
      answer: "Yes! There are no contracts or cancellation fees. You can cancel your subscription at any time from your dashboard. Your account will remain active until the end of your billing period."
    },
    {
      question: "What payment methods can I accept from clients?",
      answer: "With Pro or Elite, you can accept payments via Stripe (credit/debit cards), PayPal, Cash App, Zelle, and Venmo. All plans support multiple payment methods. You can also accept cash in person."
    },
    {
      question: "Can I transfer my data from another booking software?",
      answer: "Yes! We offer free data migration assistance for Pro and Elite members. Contact our support team at info@myhairchair.com and we'll help transfer your client list, appointment history, and settings."
    },
    {
      question: "Is my data secure?",
      answer: "Absolutely. We use bank-level 256-bit SSL encryption for all data. We're PCI DSS compliant for payment processing and HIPAA compliant for health-related client information. Your data is never sold to third parties."
    },
    {
      question: "Do I need technical skills to use MyHairChair?",
      answer: "Not at all! MyHairChair is designed to be intuitive and easy to use. Most users are fully set up within 15 minutes. We also provide free training resources and responsive support."
    },
    {
      question: "What's included in the Reports Add-On?",
      answer: "The Reports Add-On ($10/month) unlocks full analytics including all date ranges (up to 1 year), year-over-year comparisons, PDF exports, client retention insights, and detailed revenue trends. Pro users get basic 30-day reports included."
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation - Black & Gold */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                <Scissors className="w-5 h-5 text-black" />
              </div>
              <span className="font-bold text-xl bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">MyHairChair</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-300 hover:text-amber-400 transition-colors">Features</a>
              <a href="#pricing" className="text-gray-300 hover:text-amber-400 transition-colors">Pricing</a>
              <a href="#testimonials" className="text-gray-300 hover:text-amber-400 transition-colors">Success Stories</a>
              <a href="#faq" className="text-gray-300 hover:text-amber-400 transition-colors">FAQ</a>
              <Link to="/invite-stylist" className="text-purple-400 hover:text-purple-300 transition-colors">Invite Stylists</Link>
            </div>

            <div className="hidden md:flex items-center gap-4">
              {loading ? (
                <div className="w-8 h-8 rounded-full bg-amber-500/20 animate-pulse" />
              ) : user ? (
                <div className="flex items-center gap-3">
                  <Button 
                    onClick={() => navigate(user.role === "stylist" || user.role === "owner" ? "/stylist" : "/dashboard")}
                    className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black font-semibold shadow-lg shadow-amber-500/30"
                    data-testid="go-to-dashboard-btn"
                  >
                    Go to Dashboard
                  </Button>
                  <ProfileDropdown user={user} />
                </div>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10" data-testid="login-btn">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black font-semibold shadow-lg shadow-amber-500/30 px-6" data-testid="get-started-btn">
                      Get Started Free
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 text-amber-400"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 animate-fade-in border-t border-amber-500/20 mt-4">
              <div className="flex flex-col gap-4">
                <a href="#features" className="text-gray-300 hover:text-amber-400 py-2">Features</a>
                <a href="#pricing" className="text-gray-300 hover:text-amber-400 py-2">Pricing</a>
                <a href="#testimonials" className="text-gray-300 hover:text-amber-400 py-2">Success Stories</a>
                <a href="#faq" className="text-gray-300 hover:text-amber-400 py-2">FAQ</a>
                <hr className="border-amber-500/20" />
                {loading ? (
                  <div className="w-full h-10 rounded-lg bg-amber-500/20 animate-pulse" />
                ) : user ? (
                  <div className="flex flex-col gap-3">
                    <Button 
                      onClick={() => navigate(user.role === "stylist" || user.role === "owner" ? "/stylist" : "/dashboard")}
                      className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black w-full font-semibold"
                    >
                      Go to Dashboard
                    </Button>
                    <div className="flex items-center justify-center">
                      <ProfileDropdown user={user} />
                    </div>
                  </div>
                ) : (
                  <>
                    <Link to="/login" className="w-full">
                      <Button variant="outline" className="w-full border-amber-500 text-amber-400 hover:bg-amber-500/10">Sign In</Button>
                    </Link>
                    <Link to="/register" className="w-full">
                      <Button className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black w-full font-semibold">Get Started Free</Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section - Cinematic Black & Gold */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black -z-20"></div>
        
        {/* Gold accent gradients */}
        <div className="absolute top-0 left-0 w-full h-full -z-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-yellow-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-amber-400/5 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>

        {/* Decorative gold lines */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute top-20 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"></div>
          <div className="absolute bottom-20 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              {/* Keep the 100 Badge */}
              <Badge className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-400 border-amber-500/30 mb-4 px-4 py-2 text-sm font-bold">
                <DollarSign className="w-4 h-4 mr-1" />
                KEEP 100% OF YOUR EARNINGS
              </Badge>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
                <span className="bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-400 bg-clip-text text-transparent">
                  Keep the 100 💯
                </span>
                <br />
                <span className="text-white text-2xl sm:text-3xl md:text-4xl">
                  Your Money. Your Clients. Zero Fees.
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl text-amber-200/80 mb-4 font-light">
                Accept CashApp, Zelle, or Cash — we don&apos;t take a cut.
              </p>
              <p className="text-sm sm:text-base text-gray-400 mb-6 sm:mb-10">
                Want card payments? Stripe&apos;s got you covered <span className="text-gray-500">(standard fees apply)</span>
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start mb-8 sm:mb-12">
                <Link to="/register">
                  <Button className="w-full sm:w-auto bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 hover:from-amber-600 hover:via-yellow-600 hover:to-amber-600 text-black text-base sm:text-lg px-6 sm:px-10 py-4 sm:py-6 rounded-xl shadow-xl shadow-amber-500/30 font-bold" data-testid="hero-cta-btn">
                    START FREE TODAY
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    const section = document.getElementById('features');
                    if (section) {
                      section.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="w-full sm:w-auto border-amber-500/50 text-amber-400 hover:bg-amber-500/10 hover:border-amber-400 text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 rounded-xl"
                  data-testid="see-features-btn"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  See Features
                </Button>
              </div>

              {/* Quick value props */}
              <div className="flex flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start">
                {[
                  { icon: Banknote, text: "CashApp/Zelle = 0% fees" },
                  { icon: CardIcon, text: "Stripe available" },
                  { icon: Check, text: "Setup in 15 minutes" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-gray-300">
                    <div className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <item.icon className="w-3 h-3 text-amber-400" />
                    </div>
                    <span className="text-xs sm:text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Right side - Hero image carousel */}
            <div className="relative">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl shadow-amber-500/20 border border-amber-500/20">
                {heroImages.map((image, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-opacity duration-1000 ${
                      index === currentSlide ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  </div>
                ))}
                
                {/* Slide indicators */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {heroImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentSlide ? 'bg-amber-400 w-6' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </div>
              
              {/* Floating badge */}
              <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-amber-500 to-yellow-600 text-black px-4 py-2 rounded-xl shadow-lg font-semibold text-sm">
                Built by Barbers, for Barbers
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-gradient-to-r from-amber-500/10 via-yellow-500/5 to-amber-500/10 border-y border-amber-500/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                <div className="text-2xl sm:text-3xl font-bold text-white">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Enhanced with Show More */}
      <section id="features" className="py-24 bg-black relative">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center mb-16">
            <span className="text-amber-400 font-medium mb-4 block tracking-widest uppercase">Features That Make Your Life Easier</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Everything You Need to <span className="text-amber-400">Succeed</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Premium tools designed specifically for barbers, stylists, and salon owners.
            </p>
          </div>

          {/* Top 8 Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {topFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-gray-900 to-black border border-amber-500/20 rounded-2xl p-6 hover:border-amber-500/50 hover:shadow-xl hover:shadow-amber-500/10 transition-all group"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500/20 to-yellow-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:from-amber-500 group-hover:to-yellow-600 transition-all">
                  <feature.icon className="w-6 h-6 text-amber-400 group-hover:text-black transition-colors" />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-white">
                    {feature.title}
                  </h3>
                  {feature.isNew && (
                    <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-black text-xs">
                      NEW
                    </Badge>
                  )}
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* Show More Features */}
          <div className="mt-8">
            <button
              onClick={() => setShowAllFeatures(!showAllFeatures)}
              className="mx-auto flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors font-medium"
            >
              {showAllFeatures ? (
                <>
                  <ChevronUp className="w-5 h-5" />
                  Show Less Features
                </>
              ) : (
                <>
                  <ChevronDown className="w-5 h-5" />
                  Show All {moreFeatures.length + topFeatures.length} Features
                </>
              )}
            </button>
            
            {/* Expandable Features Grid */}
            {showAllFeatures && (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8 animate-fade-in">
                {moreFeatures.map((feature, index) => (
                  <div 
                    key={index}
                    className="bg-gradient-to-br from-gray-900/80 to-black/80 border border-amber-500/10 rounded-2xl p-6 hover:border-amber-500/30 transition-all group"
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500/10 to-yellow-500/5 rounded-xl flex items-center justify-center mb-4 group-hover:from-amber-500/20 group-hover:to-yellow-500/10 transition-all">
                      <feature.icon className="w-6 h-6 text-amber-400/80" />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* The Bottom Line - Transparency Section */}
      <section className="py-20 bg-gradient-to-b from-black via-gray-900/50 to-black relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-green-400 font-medium mb-4 block tracking-widest uppercase">The Bottom Line</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Transparent Pricing. <span className="text-green-400">No Surprises.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {transparencyPoints.map((point, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-green-500/10 to-emerald-500/5 border border-green-500/20 rounded-2xl p-6 text-center"
              >
                <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <point.icon className="w-7 h-7 text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{point.title}</h3>
                <p className="text-gray-400 text-sm">{point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 bg-black relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-amber-400 font-medium mb-4 block tracking-widest uppercase">Success Stories</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Trusted by <span className="text-amber-400">Thousands</span>
            </h2>
            <p className="text-gray-400">Real professionals. Real results. Real success.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-gray-900/80 to-black/80 border border-amber-500/20 p-8 rounded-2xl hover:border-amber-500/40 transition-all"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed italic">
                  &ldquo;{testimonial.quote}&rdquo;
                </p>
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover ring-2 ring-amber-500/50"
                  />
                  <div>
                    <div className="font-medium text-white">{testimonial.name}</div>
                    <div className="text-sm text-amber-400">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-gradient-to-b from-black via-gray-900/30 to-black relative">
        <div className="absolute inset-0 bg-gradient-to-t from-amber-500/5 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center mb-16">
            <span className="text-amber-400 font-medium mb-4 block tracking-widest uppercase">Simple Pricing</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Choose Your <span className="text-amber-400">Plan</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Start free and scale as you grow. No hidden fees, no booking commissions.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div 
                key={index}
                className={`rounded-2xl p-8 transition-all ${plan.popular 
                  ? 'bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-500 text-black shadow-xl shadow-amber-500/30 scale-105 ring-4 ring-amber-400/50' 
                  : 'bg-gradient-to-br from-gray-900 to-black border border-amber-500/20 hover:border-amber-500/40'}`}
                data-testid={`pricing-plan-${plan.name.toLowerCase()}`}
              >
                {plan.popular && (
                  <div className="bg-black/20 text-black text-sm font-bold px-3 py-1 rounded-full inline-block mb-4">
                    Most Popular
                  </div>
                )}
                <h3 className={`text-2xl font-bold mb-2 ${plan.popular ? 'text-black' : 'text-white'}`}>
                  {plan.name}
                </h3>
                <p className={`mb-4 ${plan.popular ? 'text-black/70' : 'text-gray-400'}`}>
                  {plan.description}
                </p>
                <div className="mb-6">
                  <span className={`text-4xl font-bold ${plan.popular ? 'text-black' : 'text-white'}`}>
                    {plan.price}
                  </span>
                  {plan.period && <span className={plan.popular ? 'text-black/70' : 'text-gray-400'}>{plan.period}</span>}
                  {plan.name === "Elite" && (
                    <p className="text-sm text-purple-400 mt-1">First 3 months, then $49.99/mo</p>
                  )}
                  {plan.name === "Pro" && (
                    <p className="text-sm text-amber-600 mt-1">First 3 months, then $29.99/mo</p>
                  )}
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className={`flex items-center gap-3 ${plan.popular ? 'text-black' : 'text-gray-300'}`}>
                      <Check className={`w-5 h-5 flex-shrink-0 ${plan.popular ? 'text-black' : 'text-amber-400'}`} />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className={`w-full rounded-xl font-semibold ${plan.popular 
                    ? 'bg-black text-amber-400 hover:bg-gray-900' 
                    : 'bg-gradient-to-r from-amber-500 to-yellow-600 text-black hover:from-amber-600 hover:to-yellow-700'}`}
                  onClick={() => navigate("/register")}
                >
                  Start 30-Day Trial
                </Button>
              </div>
            ))}
          </div>

          {/* Add-On Callout */}
          <div className="mt-12 max-w-2xl mx-auto">
            <div className="bg-gradient-to-r from-purple-500/20 to-violet-500/20 border border-purple-500/30 rounded-2xl p-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <BarChart3 className="w-5 h-5 text-purple-400" />
                <span className="text-purple-400 font-semibold">Reports Add-On</span>
              </div>
              <p className="text-gray-300 text-sm mb-3">
                Unlock full analytics with all date ranges, YoY comparisons, and PDF exports for just <strong className="text-white">$10/month</strong>
              </p>
              <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">30-Day Free Trial</Badge>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-black relative">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-amber-400 font-medium mb-4 block tracking-widest uppercase">Got Questions?</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Frequently Asked <span className="text-amber-400">Questions</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-gray-900 to-black border border-amber-500/20 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-amber-500/5 transition-colors"
                >
                  <span className="font-medium text-white pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-amber-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-amber-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 text-gray-400 text-sm leading-relaxed animate-fade-in">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contact Support */}
          <div className="mt-12 text-center">
            <p className="text-gray-400 mb-4">Still have questions?</p>
            <a 
              href="mailto:info@myhairchair.com"
              className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 font-medium"
            >
              <Mail className="w-5 h-5" />
              info@myhairchair.com
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-black/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-black/10 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <h2 className="text-3xl sm:text-5xl font-bold text-black mb-6">
            Ready to Elevate Your Business?
          </h2>
          <p className="text-black/70 text-lg mb-10 max-w-2xl mx-auto">
            Join thousands of beauty professionals who&apos;ve taken control of their success. 
            Start free today - no credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="bg-black hover:bg-gray-900 text-amber-400 text-lg px-12 py-6 rounded-xl shadow-xl font-bold">
                GET STARTED FREE
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="#pricing">
              <Button variant="outline" className="border-black/50 text-black hover:bg-black/10 text-lg px-8 py-6 rounded-xl font-semibold">
                View Pricing
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Enhanced Footer */}
      <footer className="bg-black text-gray-400 py-16 border-t border-amber-500/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-12 mb-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 rounded-xl flex items-center justify-center">
                  <Scissors className="w-5 h-5 text-black" />
                </div>
                <span className="font-bold text-xl bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">MyHairChair</span>
              </div>
              <p className="text-sm text-gray-500 mb-4 max-w-xs">
                Empowering beauty professionals with premium tools for booking, payments, and client loyalty.
              </p>
              <p className="text-sm text-gray-500">
                <strong className="text-amber-400">Contact:</strong> info@myhairchair.com
              </p>
            </div>

            {/* For Professionals */}
            <div>
              <h4 className="font-medium text-white mb-4">For Professionals</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-amber-400 transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-amber-400 transition-colors">Pricing</a></li>
                <li><Link to="/register" className="hover:text-amber-400 transition-colors">Get Started</Link></li>
                <li><Link to="/login" className="hover:text-amber-400 transition-colors">Sign In</Link></li>
                <li><Link to="/guide" className="hover:text-amber-400 transition-colors">User Guide</Link></li>
              </ul>
            </div>

            {/* For Clients */}
            <div>
              <h4 className="font-medium text-white mb-4">For Clients</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="hover:text-amber-400 transition-colors">Find a Stylist</Link></li>
                <li><Link to="/register" className="hover:text-amber-400 transition-colors">Create Account</Link></li>
                <li><Link to="/login" className="hover:text-amber-400 transition-colors">Book Appointment</Link></li>
                <li><a href="#" className="hover:text-amber-400 transition-colors">Loyalty Rewards</a></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-medium text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-amber-400 transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-amber-400 transition-colors">Careers</a></li>
                <li><Link to="/faq" className="hover:text-amber-400 transition-colors">FAQ</Link></li>
                <li><Link to="/legal?tab=privacy" className="hover:text-amber-400 transition-colors">Privacy Policy</Link></li>
                <li><Link to="/legal?tab=terms" className="hover:text-amber-400 transition-colors">Terms of Service</Link></li>
                <li><Link to="/legal?tab=refund" className="hover:text-amber-400 transition-colors">Refund Policy</Link></li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-amber-500/20 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <p className="text-sm text-gray-500">
                © 2026 MyHairChair, LLC. Your Chair. Your Rules. Your Rewards.
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Headquartered in Houston, Texas • info@haircare.com
              </p>
            </div>
            <div className="flex items-center gap-4">
              <a href="#" className="text-gray-500 hover:text-amber-400 transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-500 hover:text-amber-400 transition-colors">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-500 hover:text-amber-400 transition-colors">
                <Twitter className="w-5 h-5" />
              </a>
              <a href="#" className="text-gray-500 hover:text-amber-400 transition-colors">
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Custom animation styles */}
      <style>{`
        @keyframes scroll {
          0% { transform: translateY(0); opacity: 1; }
          100% { transform: translateY(6px); opacity: 0; }
        }
        .animate-scroll {
          animation: scroll 1.5s ease-in-out infinite;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>

      {/* App Introduction Modal */}
      <Dialog open={showIntro} onOpenChange={setShowIntro}>
        <DialogContent className="max-w-2xl bg-transparent border-none p-0 shadow-none" aria-describedby="intro-landing-desc">
          <p id="intro-landing-desc" className="sr-only">Introduction to MyHairChair platform</p>
          <AppIntroduction 
            onComplete={() => setShowIntro(false)}
            autoPlay={true}
            showCloseButton={true}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
